import React, { useRef, useState, useEffect } from "react";
import { Eraser, RotateCcw, Download } from "lucide-react";
import { KOLAM_TRACE } from "../../data/studio";
import ShareArtwork from "./ShareArtwork";

const BRUSH_COLORS = [
  { name: "Rice flour", value: "#FFFFFF" },
  { name: "Marigold", value: "#F5B400" },
  { name: "Terracotta", value: "#D95D39" },
  { name: "Rose", value: "#E54B6A" },
  { name: "Indigo", value: "#1E2A4F" },
];

const CANVAS_SIZE = 600;

export default function TraceKolam({ onSaved }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [brushColor, setBrushColor] = useState(BRUSH_COLORS[1].value);
  const [brushSize, setBrushSize] = useState(8);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = CANVAS_SIZE;
    c.height = CANVAS_SIZE;
    const ctx = c.getContext("2d");
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // One-time canvas setup on mount; CANVAS_SIZE is a stable module-level const.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * CANVAS_SIZE,
      y: ((clientY - rect.top) / rect.height) * CANVAS_SIZE,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const pos = getPos(e);
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
  };

  const end = () => {
    drawing.current = false;
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  };

  const downloadTrace = () => {
    const c = canvasRef.current;
    // composite: paint the cream + dots + drawing on a new canvas
    const out = document.createElement("canvas");
    out.width = CANVAS_SIZE;
    out.height = CANVAS_SIZE;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#1E2A4F";
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    // dots
    const scale = CANVAS_SIZE / 400;
    ctx.fillStyle = "#F9F6F0";
    KOLAM_TRACE.dots.forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x * scale, y * scale, 5 * scale, 0, Math.PI * 2);
      ctx.fill();
    });
    // user trace
    ctx.drawImage(c, 0, 0);
    const link = document.createElement("a");
    link.download = "my-kolam-trace.png";
    link.href = out.toDataURL("image/png");
    link.click();
    if (onSaved) onSaved();
  };

  return (
    <div data-testid="trace-kolam" className="grid lg:grid-cols-12 gap-8 items-start">
      {/* Canvas + dots overlay */}
      <div className="lg:col-span-8">
        <div className="relative w-full aspect-square rounded-2xl overflow-hidden border-4 border-[#E5DFD3] bg-[#1E2A4F] shadow-xl">
          {/* Faint guide curves */}
          <svg
            viewBox="0 0 400 400"
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
          >
            {KOLAM_TRACE.guides.map((d, i) => (
              <path
                key={i}
                d={d}
                fill="none"
                stroke="rgba(249, 246, 240, 0.18)"
                strokeWidth="2"
                strokeDasharray="6 4"
              />
            ))}
            {KOLAM_TRACE.dots.map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="5" fill="#F9F6F0" />
            ))}
          </svg>
          {/* Drawing canvas */}
          <canvas
            ref={canvasRef}
            data-testid="trace-canvas"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
          />
        </div>
        <p className="mt-4 text-sm text-[#5C6A8F] text-center">
          Tip: Start at any dot and curl the line around it — try not to lift your finger!
        </p>
      </div>

      {/* Tools */}
      <div className="lg:col-span-4 space-y-6">
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Pick a colour</p>
          <div className="flex flex-wrap gap-2">
            {BRUSH_COLORS.map((c) => (
              <button
                key={c.value}
                data-testid={`trace-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setBrushColor(c.value)}
                aria-label={c.name}
                style={{ background: c.value }}
                className={`w-12 h-12 rounded-full border-2 transition-transform duration-300 ${
                  brushColor === c.value
                    ? "scale-110 border-[#1E2A4F] ring-2 ring-[#1E2A4F]/30 ring-offset-2 ring-offset-[#F9F6F0]"
                    : "border-[#E5DFD3] hover:scale-105"
                }`}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Brush size</p>
          <input
            type="range"
            min="2"
            max="24"
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value, 10))}
            data-testid="trace-brush-size"
            className="w-full accent-[#D95D39]"
          />
          <div className="flex items-center gap-2 text-xs text-[#5C6A8F] mt-1">
            <span style={{ background: brushColor, width: brushSize, height: brushSize, borderRadius: "50%" }} />
            <span>{brushSize}px</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="trace-clear"
            onClick={clear}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
          >
            <RotateCcw size={14} /> Start over
          </button>
          <button
            data-testid="trace-erase"
            onClick={() => setBrushColor("#1E2A4F")}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
          >
            <Eraser size={14} /> Erase
          </button>
        </div>

        <button
          data-testid="trace-download"
          onClick={downloadTrace}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full bg-[#D95D39] text-[#F9F6F0] hover:bg-[#1E2A4F] transition-colors duration-300 text-sm font-medium tracking-wide"
        >
          <Download size={16} /> Save my kolam trace
        </button>
        <ShareArtwork
          artFormId="kolam"
          artName="Kolam"
          accent="#D95D39"
          testIdPrefix="trace-kolam"
        />
      </div>
    </div>
  );
}
